/**
 * SectionErrorBoundary Component
 *
 * A class-based error boundary for settings sections that catches render errors
 * and displays a Polaris-styled fallback without breaking the entire page.
 *
 * Design Compliance:
 * - Uses Polaris Banner with critical status
 * - Maintains layout consistency (InlineGrid pattern)
 * - Provides actionable retry option
 * - Graceful degradation without affecting other sections
 *
 * @example
 * ```tsx
 * <SectionErrorBoundary sectionName="Email Settings">
 *   <EmailSettingsSection {...props} />
 * </SectionErrorBoundary>
 * ```
 */

import { Component, type ReactNode } from 'react';
import { Banner, BlockStack, Text, InlineGrid, Box, Card, Button } from '@shopify/polaris';
import { reportError } from '../../utils/error-reporter';

export interface SectionErrorBoundaryProps {
  /** Name of the section for error messaging */
  sectionName: string;
  /** Children to render */
  children: ReactNode;
  /** Optional callback when error occurs */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface SectionErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Class-based Error Boundary for settings sections
 * React requires class components for error boundaries (no hooks equivalent)
 */
export class SectionErrorBoundary extends Component<
  SectionErrorBoundaryProps,
  SectionErrorBoundaryState
> {
  constructor(props: SectionErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): SectionErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Error caught in section boundary - report to error tracking

    // Report to error tracking
    reportError(error, {
      component: 'SectionErrorBoundary',
      section: this.props.sectionName,
      type: 'render-error',
    });

    // Call optional error callback
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <InlineGrid columns={{ xs: '1fr', md: '1fr 2fr' }} gap="400">
          {/* Left column - Section header placeholder */}
          <Box>
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd">
                {this.props.sectionName}
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                This section encountered an error
              </Text>
            </BlockStack>
          </Box>

          {/* Right column - Error state */}
          <Card roundedAbove="sm">
            <BlockStack gap="400">
              <Banner
                title="Unable to load this section"
                tone="critical"
              >
                <BlockStack gap="200">
                  <Text as="p" variant="bodyMd">
                    Something went wrong while loading {this.props.sectionName.toLowerCase()}.
                    This error has been logged for investigation.
                  </Text>
                  {this.state.error?.message && (
                    <Text as="p" variant="bodySm" tone="subdued">
                      Error: {this.state.error.message}
                    </Text>
                  )}
                </BlockStack>
              </Banner>
              <Button onClick={this.handleRetry}>
                Try again
              </Button>
            </BlockStack>
          </Card>
        </InlineGrid>
      );
    }

    return this.props.children;
  }
}
